import React from 'react';
import { useCurrentFrame, interpolate } from 'remotion';

const COLORS = {
  aiBlue: '#3b82f6',
  brokerGray: '#475569',
  green: '#059669',
};

// Property data for the spreadsheet row
const PROPERTY_DATA = {
  address: '890 Distribution Blvd',
  city: 'Evans',
  sf: '45,000',
  rent: '$4.50',
  nnn: '$1.20',
  docks: '6',
  height: "28'",
  power: '800A',
  flyer: 'View PDF',
  comments: 'Rail spur access, fenced yard',
};

const COLUMNS = ['Property', 'City', 'SF', 'Rent', 'NNN', 'Docks', 'Ht', 'Power', 'Flyer', 'Comments'];

const THREAD1 = {
  property: '4521 Commerce Way, Augusta',
  aiOutreach: {
    from: 'AI Assistant',
    time: '9:14 AM',
    text: "Hi Marcus, I found your listing at 4521 Commerce Way. I represent a company looking for industrial space. Could you share the SF, rent, NNN, and loading details?",
  },
  brokerReply: {
    from: 'Marcus Webb',
    time: '2:23 PM',
    text: "Sorry, this property is unavailable. But I have another property that fits your specs - reach out to Sarah Chen about 890 Distribution Blvd in Evans.",
  },
  events: ['Property unavailable', 'New property detected'],
  aiResponse: {
    from: 'AI Assistant',
    time: '2:24 PM',
    text: "Thanks for letting me know. If you have any other properties that might be similar and fit the specs, please let me know!",
  },
};

const THREAD2 = {
  property: '890 Distribution Blvd, Evans',
  aiOutreach: {
    from: 'AI Assistant',
    time: '2:25 PM',
    text: "Hi Sarah, Marcus Webb mentioned you have a listing at 890 Distribution Blvd that might fit our needs. Could you share details on SF, rent, docks, and power?",
  },
  brokerReply: {
    from: 'Sarah Chen',
    time: '3:41 PM',
    text: "Here are the details: 45,000 SF, $4.50/SF rent, $1.20 NNN. 6 dock doors, 28' clear, 800A power. Rail spur access.",
    attachment: '890_Distribution_Brochure.pdf',
  },
  aiClosing: {
    from: 'AI Assistant',
    time: '3:42 PM',
    text: "Thank you for the details! I'll share this with my client and follow up shortly.",
  },
};

const EmailCard = ({ from, time, text, attachment, isAI, opacity = 1 }) => (
  <div style={{
    background: '#ffffff',
    borderRadius: 12,
    padding: '16px 20px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    border: `1px solid ${isAI ? 'rgba(59, 130, 246, 0.2)' : '#e5e7eb'}`,
    borderLeft: `5px solid ${isAI ? COLORS.aiBlue : COLORS.brokerGray}`,
    maxWidth: 420,
    opacity,
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: isAI ? 'linear-gradient(135deg, #3b82f6, #8b5cf6)' : '#e5e7eb',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: isAI ? 'white' : '#6b7280', fontSize: 12, fontWeight: 600,
        }}>
          {isAI ? '✉️' : from.split(' ').map(n => n[0]).join('')}
        </div>
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#111827' }}>{from}</div>
          {isAI && <div style={{ fontSize: 11, color: '#6b7280' }}>via AI</div>}
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#9ca3af' }}>{time}</div>
    </div>
    <div style={{ fontSize: 14, color: '#374151', lineHeight: 1.6 }}>{text}</div>
    {attachment && (
      <div style={{
        marginTop: 12, padding: '10px 14px', background: '#f8fafc',
        borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 13, color: '#64748b', border: '1px solid #e2e8f0',
      }}>
        <span>📎</span>
        <span>{attachment}</span>
      </div>
    )}
  </div>
);

const SpreadsheetRow = ({ filled = false, opacity = 1 }) => {
  const gridCols = '160px 70px 70px 55px 55px 50px 40px 60px 75px 220px';

  return (
    <div style={{
      background: '#ffffff',
      borderRadius: 10,
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      width: 855,
      opacity,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
        borderBottom: '2px solid #1d4ed8',
      }}>
        {COLUMNS.map((col, i) => (
          <div key={i} style={{
            padding: '10px 6px',
            fontSize: 13,
            fontWeight: 600,
            color: 'white',
            textAlign: 'center',
            borderRight: i < COLUMNS.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none',
          }}>
            {col}
          </div>
        ))}
      </div>
      {/* Data Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: gridCols,
        background: filled ? 'rgba(5, 150, 105, 0.08)' : 'white',
      }}>
        <div style={{
          padding: '12px 6px',
          fontSize: 13,
          color: filled ? COLORS.green : '#1e293b',
          fontWeight: 500,
          borderRight: '1px solid #e2e8f0',
        }}>
          {PROPERTY_DATA.address}
        </div>
        {['city', 'sf', 'rent', 'nnn', 'docks', 'height', 'power', 'flyer', 'comments'].map((field, i) => (
          <div key={field} style={{
            padding: '12px 6px',
            fontSize: 13,
            textAlign: field === 'comments' ? 'left' : 'center',
            color: filled ? (field === 'flyer' ? '#3b82f6' : COLORS.green) : '#94a3b8',
            borderRight: i < 8 ? '1px solid #e2e8f0' : 'none',
            fontWeight: filled ? 500 : 400,
            textDecoration: filled && field === 'flyer' ? 'underline' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}>
            {filled ? PROPERTY_DATA[field] : '—'}
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * ConversationAnimation - Updated flow
 *
 * Thread 1 (~12 sec):
 * - Frame 0: AI outreach visible
 * - Frames 0-90: Broker reply (unavailable + new property suggestion)
 * - Frames 90-180: Event badges (Property unavailable + New property detected)
 * - Frames 180-270: AI response (thanks, let me know)
 * - Frames 270-360: Hold
 *
 * Transition (Frames 360-450)
 *
 * Thread 2 (~18 sec):
 * - Frames 450-540: AI outreach to new contact
 * - Frames 540-630: Broker reply WITH PDF attachment
 * - Frames 630-720: Extraction badges (fields + PDF extracted)
 * - Frames 720-810: Spreadsheet row fills
 * - Frames 810-870: AI closing
 * - Frames 870-930: Complete badge
 */
export const ConversationAnimation = () => {
  const frame = useCurrentFrame();
  const isSettled = frame >= 1230;

  // Thread 1 (frames 0-270, then hold for 300 frames = 10 sec)
  const t1_brokerReply = isSettled ? 1 : interpolate(frame, [0, 90], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const t1_events = isSettled ? 1 : interpolate(frame, [90, 180], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const t1_aiResponse = isSettled ? 1 : interpolate(frame, [180, 270], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  // Transition (after 10 sec hold: frames 570-660)
  const transition = isSettled ? 1 : interpolate(frame, [570, 660], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const showThread1 = frame < 660;
  const showThread2 = frame >= 570 || isSettled;

  // Thread 2 (starts at 660)
  const t2_aiOutreach = isSettled ? 1 : interpolate(frame, [660, 750], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const t2_brokerReply = isSettled ? 1 : interpolate(frame, [750, 840], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const t2_extraction = isSettled ? 1 : interpolate(frame, [840, 930], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const t2_rowFilled = frame >= 930 || isSettled;
  const t2_aiClosing = isSettled ? 1 : interpolate(frame, [1020, 1080], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const t2_complete = isSettled ? 1 : interpolate(frame, [1140, 1200], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });

  return (
    <div style={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '24px 40px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      {/* Thread 1 */}
      {showThread1 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: 1 - transition }}>
          <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
            Thread: {THREAD1.property}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <EmailCard {...THREAD1.aiOutreach} isAI={true} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', opacity: t1_brokerReply }}>
            <EmailCard {...THREAD1.brokerReply} isAI={false} />
          </div>
          {/* Event badges */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 12, opacity: t1_events,
            transform: `translateY(${interpolate(t1_events, [0, 1], [10, 0])}px)`,
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
              padding: '8px 16px', borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 500,
            }}>
              ⚠️ Property unavailable
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              padding: '8px 16px', borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 500,
            }}>
              ✨ New property detected
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', opacity: t1_aiResponse }}>
            <EmailCard {...THREAD1.aiResponse} isAI={true} />
          </div>
        </div>
      )}

      {/* Transition */}
      {transition > 0 && transition < 1 && (
        <div style={{
          display: 'flex', justifyContent: 'center', padding: '24px 0',
          opacity: Math.sin(transition * Math.PI),
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
            padding: '12px 24px', borderRadius: 10, color: 'white', fontSize: 14, fontWeight: 600,
          }}>
            Starting outreach to new contact...
          </div>
        </div>
      )}

      {/* Thread 2 */}
      {showThread2 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, opacity: transition }}>
          <div style={{ textAlign: 'center', color: '#64748b', fontSize: 13, fontWeight: 500 }}>
            Thread: {THREAD2.property}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', opacity: t2_aiOutreach }}>
            <EmailCard {...THREAD2.aiOutreach} isAI={true} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', opacity: t2_brokerReply }}>
            <EmailCard {...THREAD2.brokerReply} isAI={false} />
          </div>

          {/* Extraction badges */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: 12, opacity: t2_extraction,
            transform: `translateY(${interpolate(t2_extraction, [0, 1], [10, 0])}px)`,
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #059669, #10b981)',
              padding: '8px 16px', borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 500,
            }}>
              ✓ 6 fields extracted
            </div>
            <div style={{
              background: 'linear-gradient(135deg, #0891b2, #06b6d4)',
              padding: '8px 16px', borderRadius: 8, color: 'white', fontSize: 13, fontWeight: 500,
            }}>
              📄 PDF downloaded & extracted
            </div>
          </div>

          {/* Spreadsheet Row */}
          <div style={{
            display: 'flex', justifyContent: 'center',
            opacity: t2_extraction,
            transform: `translateY(${interpolate(t2_extraction, [0, 1], [15, 0])}px)`,
          }}>
            <SpreadsheetRow filled={t2_rowFilled} />
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', opacity: t2_aiClosing }}>
            <EmailCard {...THREAD2.aiClosing} isAI={true} />
          </div>

          <div style={{
            display: 'flex', justifyContent: 'center', opacity: t2_complete,
            transform: `translateY(${interpolate(t2_complete, [0, 1], [10, 0])}px)`,
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #8b5cf6, #a855f7)',
              padding: '12px 28px', borderRadius: 24, boxShadow: '0 4px 12px rgba(139,92,246,0.4)',
            }}>
              <span style={{ color: 'white', fontSize: 15, fontWeight: 600 }}>✓ Conversation Complete</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationAnimation;
